/**
 * Reader preference for hub mosaic video motion. Stored only when off, so the
 * default (on) needs no storage. `prefers-reduced-motion` and short viewports
 * (below the mosaic floor) force stacked / frozen media separately — the
 * switch is hidden in those cases.
 *
 * Pattern copied from Kicker `motionPreference` (verge:kicker-motion) — hub key
 * is separate so the homepage Kicker and this story don’t fight.
 */

import { useSyncExternalStore } from "react";

import { useHubShortViewport } from "@story/data-centers-hub/hooks/useHubShortViewport";
import { usePrefersReducedMotion } from "@story/data-centers-hub/hooks/usePrefersReducedMotion";

export const HUB_MOTION_STORAGE_KEY = "verge:data-centers-hub-motion";

const listeners = new Set<() => void>();

// Set only when a storage write fails, so the switch still works for the
// rest of the page view even though the choice can't be remembered.
let unsavedEnabled: boolean | undefined;

let storageListening = false;

const notify = () => {
  listeners.forEach((listener) => listener());
};

const onStorage = (event: StorageEvent) => {
  if (event.key === HUB_MOTION_STORAGE_KEY || event.key === null) {
    notify();
  }
};

const ensureStorageListener = () => {
  if (storageListening || typeof window === "undefined") {
    return;
  }
  window.addEventListener("storage", onStorage);
  storageListening = true;
};

const releaseStorageListener = () => {
  if (!storageListening || listeners.size > 0) {
    return;
  }
  window.removeEventListener("storage", onStorage);
  storageListening = false;
};

export const readHubMotionEnabled = (): boolean => {
  if (unsavedEnabled !== undefined) {
    return unsavedEnabled;
  }

  try {
    return window.localStorage.getItem(HUB_MOTION_STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
};

export const writeHubMotionEnabled = (enabled: boolean): void => {
  try {
    if (enabled) {
      window.localStorage.removeItem(HUB_MOTION_STORAGE_KEY);
    } else {
      window.localStorage.setItem(HUB_MOTION_STORAGE_KEY, "off");
    }
    unsavedEnabled = undefined;
  } catch {
    unsavedEnabled = enabled;
  }

  notify();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  ensureStorageListener();

  return () => {
    listeners.delete(listener);
    releaseStorageListener();
  };
};

/** `[enabled, setEnabled]` for hub mosaic / wipe motion (nav play/pause). */
export const useHubMotionEnabled = (): [
  boolean,
  (enabled: boolean) => void,
] => [
  useSyncExternalStore(subscribe, readHubMotionEnabled, () => true),
  writeHubMotionEnabled,
];

/**
 * Effective snap / wipe / live-media mode. Off when the reader paused motion,
 * OS reduced-motion is on, or the viewport is shorter than the mosaic floor.
 */
export function useHubMotionOn(): boolean {
  const [motionPref] = useHubMotionEnabled();
  const prefersReducedMotion = usePrefersReducedMotion();
  const shortViewport = useHubShortViewport();
  return motionPref && !prefersReducedMotion && !shortViewport;
}
